/**
 * Employee Create Page — hardcoded form, no schema dependency.
 * Calls POST /v1/hros/employees via BLL.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import { useCreate } from '@refinedev/core';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Input, Label,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'intern', 'consultant'];
const EMPLOYMENT_STATUSES = ['active', 'probation', 'on_leave', 'terminated', 'resigned'];
const GENDERS = ['male', 'female', 'other'];

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  employee_code: '',
  date_of_birth: '',
  gender: '',
  blood_group: '',
  marital_status: '',
  date_of_joining: '',
  date_of_confirmation: '',
  employment_type: 'full_time',
  employment_status: 'active',
  current_address: '',
  city: '',
  state: '',
  pincode: '',
  pan: '',
  aadhaar: '',
  uan: '',
  pf_number: '',
};

export function EmployeeCreatePage() {
  const navigate = useNavigate();
  const { mutate: create, isLoading } = useCreate();
  const [form, setForm] = React.useState<Record<string, string>>({ ...INITIAL_FORM });

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // strip empty strings so the API gets clean data
    const values: Record<string, string> = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== ''),
    );
    create(
      { resource: 'employees', values },
      {
        onSuccess: (data) => {
          navigate(`/employees/${data.data.id}`);
        },
      },
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/employees')} disabled={isLoading}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Directory
        </Button>
      </div>

      <h1 className="text-2xl font-semibold">Add Employee</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader><CardTitle>Basic Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="first_name">First Name <span className="text-destructive">*</span></Label>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={set('first_name')}
                  placeholder="First name"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={set('last_name')}
                  placeholder="Last name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="email">Work Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="employee@company.com"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="employee_code">Employee Code</Label>
                <Input
                  id="employee_code"
                  value={form.employee_code}
                  onChange={set('employee_code')}
                  placeholder="EMP001"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={form.date_of_birth}
                  onChange={set('date_of_birth')}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="gender">Gender</Label>
                <select
                  id="gender"
                  value={form.gender}
                  onChange={set('gender')}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Select gender</option>
                  {GENDERS.map((g) => (
                    <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="blood_group">Blood Group</Label>
                <Input
                  id="blood_group"
                  value={form.blood_group}
                  onChange={set('blood_group')}
                  placeholder="O+, A-, etc."
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="marital_status">Marital Status</Label>
                <select
                  id="marital_status"
                  value={form.marital_status}
                  onChange={set('marital_status')}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Select</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employment Details */}
        <Card>
          <CardHeader><CardTitle>Employment Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="date_of_joining">Date of Joining <span className="text-destructive">*</span></Label>
                <Input
                  id="date_of_joining"
                  type="date"
                  value={form.date_of_joining}
                  onChange={set('date_of_joining')}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="date_of_confirmation">Confirmation Date</Label>
                <Input
                  id="date_of_confirmation"
                  type="date"
                  value={form.date_of_confirmation}
                  onChange={set('date_of_confirmation')}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="employment_type">Employment Type</Label>
                <select
                  id="employment_type"
                  value={form.employment_type}
                  onChange={set('employment_type')}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {EMPLOYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="employment_status">Status</Label>
                <select
                  id="employment_status"
                  value={form.employment_status}
                  onChange={set('employment_status')}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {EMPLOYMENT_STATUSES.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader><CardTitle>Address</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="current_address">Current Address</Label>
              <Input
                id="current_address"
                value={form.current_address}
                onChange={set('current_address')}
                placeholder="Street address, building, etc."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="city">City</Label>
                <Input id="city" value={form.city} onChange={set('city')} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="state">State</Label>
                <Input id="state" value={form.state} onChange={set('state')} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pincode">Pincode</Label>
                <Input id="pincode" value={form.pincode} onChange={set('pincode')} maxLength={6} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Government IDs */}
        <Card>
          <CardHeader><CardTitle>Government IDs</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="pan">PAN</Label>
                <Input
                  id="pan"
                  value={form.pan}
                  onChange={(e) => setForm((p) => ({ ...p, pan: e.target.value.toUpperCase() }))}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="aadhaar">Aadhaar</Label>
                <Input
                  id="aadhaar"
                  value={form.aadhaar}
                  onChange={set('aadhaar')}
                  placeholder="12 digit number"
                  maxLength={12}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="uan">UAN</Label>
                <Input id="uan" value={form.uan} onChange={set('uan')} maxLength={12} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pf_number">PF Number</Label>
                <Input id="pf_number" value={form.pf_number} onChange={set('pf_number')} />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pb-6">
          <Button type="button" variant="outline" onClick={() => navigate('/employees')}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create Employee'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default EmployeeCreatePage;
